import { ApiService } from '../ApiService';
import type { Declaration, DeclarationAttachment } from '../../../types/declarations';
import type { PaginatedResponse } from '../types';
import { CloudinaryService } from '../../cloudinary/CloudinaryService';

interface DeclarationFilters {
  type?: string;
  category?: string;
  status?: string;
  period?: string;
  fiscalYearId?: string;
  page?: number;
  pageSize?: number;
}

export const declarationsApi = {
  // CRUD Operations
  getAll: (filters?: DeclarationFilters) => 
    ApiService.get<PaginatedResponse<Declaration>>('/declarations', filters),

  getById: (id: string) => 
    ApiService.get<Declaration>(`/declarations/${id}`),

  create: (declaration: Partial<Declaration>) => 
    ApiService.post<Declaration>('/declarations', declaration),

  update: (id: string, declaration: Partial<Declaration>) => 
    ApiService.patch<Declaration>(`/declarations/${id}`, declaration),

  // NOTE: Le backend n'expose pas de DELETE /declarations/:id
  // La suppression passe par @Patch(':id/status') en mettant status = annulé si nécessaire

  // Status Management
  submit: (id: string) => 
    ApiService.post<Declaration>(`/declarations/${id}/submit`, {}),

  validate: (id: string, validatorId: string) => 
    ApiService.post<Declaration>(`/declarations/${id}/validate`, { validatorId }),

  reject: (id: string, reason: string) => 
    ApiService.post<Declaration>(`/declarations/${id}/reject`, { reason }),

  // Document Management - Upload vers Cloudinary puis envoi de l'URL au backend
  uploadJustification: async (id: string, file: File) => {
    const result = await CloudinaryService.uploadDeclarationDocument(file);
    return ApiService.post<{ url: string }>(`/declarations/${id}/justification`, { 
      url: result.url,
      name: file.name,
      size: result.size
    });
  },

  /** Persister une URL Cloudinary déjà obtenue comme justificatif */
  patch_justification: (id: string, url: string) =>
    ApiService.post<{ url: string }>(`/declarations/${id}/justification`, { url }),

  uploadDeclarationForm: async (id: string, file: File) => {
    const result = await CloudinaryService.uploadDeclarationDocument(file);
    return ApiService.post<{ url: string }>(`/declarations/${id}/declaration-form`, { 
      url: result.url,
      name: file.name,
      size: result.size
    });
  },

  /** Persister une URL Cloudinary déjà obtenue comme formulaire de déclaration */
  patch_declarationForm: (id: string, url: string) =>
    ApiService.post<{ url: string }>(`/declarations/${id}/declaration-form`, { url }),

  uploadAttachment: async (id: string, file: File, type: DeclarationAttachment['type']) => {
    const result = await CloudinaryService.uploadDeclarationDocument(file);
    return ApiService.post<DeclarationAttachment>(`/declarations/${id}/attachments`, {
      name: file.name,
      type,
      url: result.url,
      size: result.size,
      uploadedAt: new Date().toISOString()
    });
  },

  getAttachments: (id: string) => 
    ApiService.get<DeclarationAttachment[]>(`/declarations/${id}/attachments`),

  deleteAttachment: (declarationId: string, attachmentId: string) => 
    ApiService.delete(`/declarations/${declarationId}/attachments/${attachmentId}`),

  // Export & Download
  download: (id: string, format: 'pdf' | 'excel') => 
    ApiService.get<Blob>(`/declarations/${id}/download?format=${format}`),

  downloadJustification: (id: string) => 
    ApiService.get<Blob>(`/declarations/${id}/justification/download`),

  downloadDeclarationForm: (id: string) => 
    ApiService.get<Blob>(`/declarations/${id}/declaration-form/download`),

  downloadAttachment: (declarationId: string, attachmentId: string) => 
    ApiService.get<Blob>(`/declarations/${declarationId}/attachments/${attachmentId}/download`),

  // Reporting
  getStatistics: (period?: string) => 
    ApiService.get<{
      totalDeclarations: number;
      totalAmount: number;
      byStatus: Record<string, number>;
      byType: Record<string, number>;
      overdueCount: number;
    }>('/declarations/statistics', { period }),

  // Templates & Forms
  getDeclarationTemplate: (type: string) => 
    ApiService.get<Blob>(`/declarations/templates/${type}`),

  // Validation
  validateDeclarationData: (data: Partial<Declaration>) => 
    ApiService.post<{ valid: boolean; errors: string[] }>('/declarations/validate', data)
};